import React from 'react';
import clsx from 'clsx';
import styles from './PrintAppointmentTransportation.module.scss';
import { PrintBookingDetailAppointmentViewModel } from '../utils/PrintBookingDetailViewModel';
import { Vehicle as VehicleModel } from '@spike/vehicle-model';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

type Props = {
    appointment: PrintBookingDetailAppointmentViewModel;
    dateLabel?: string;
    className?: string;
    showDivider?: boolean;
};

export const PrintAppointmentTransportation: React.FC<Props> = ({
    appointment,
    dateLabel,
    className,
    showDivider = true
}) => {
    const vehicles = useSelector<RootState, Array<VehicleModel>>(state => state.vehicles.vehicles);

    // For transportation we expect appointment.raw to include pickup/drop details or vehicle
    const service = appointment.serviceName ?? '';

    // Use raw container (may be mergedPair for merged transports)
    const fullRaw: any = appointment.raw ?? {};
    const mergedPair: any[] | undefined = fullRaw.mergedPair;

    const staff = appointment.staffName ?? '';

    // Helper to format time range safely
    const formatRange = (rawItem: any) => {
        try {
            const from = rawItem?.appointment?.duration?.from;
            const to = rawItem?.appointment?.duration?.to;
            if (from && to) return `${from.format('h:mma')} - ${to.format('h:mma')}`;
        } catch (e) {
            // ignore
        }
        return '';
    };

    // Helper to resolve vehicle name
    const resolveVehicleName = (vehicleId: any) => {
        if (!vehicleId) return 'Unassigned';
        const v = vehicles.find(x => x.id === vehicleId);
        return v?.name ?? 'Unassigned';
    };

    return (
        <div className={clsx(styles.root, className, { [styles.withDivider]: showDivider })}>
            {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}

            <div className={styles.transportGrid}>
                {Array.isArray(mergedPair) && mergedPair.length >= 2
                    ? // merged: render both pickup and dropoff side-by-side
                      mergedPair.map((rawItem: any, idx: number) => {
                          const t = rawItem?.appointment?.details?.transportation?.type;
                          const label =
                              t === 'pickup' ? 'Pick Up' : t === 'dropoff' ? 'Drop Off' : `Transport ${idx + 1}`;
                          const timeRange = formatRange(rawItem);
                          const vehicleId = rawItem?.appointment?.details?.transportation?.vehicleId;
                          const vehicleName = resolveVehicleName(vehicleId);

                          return (
                              <div key={idx}>
                                  <p className={styles.label}>{label}</p>
                                  <p className={styles.time}>{timeRange}</p>
                                  <p className={styles.location}>{service}</p>
                                  <div className={styles.meta}>
                                      <div className={styles.metaItem}>
                                          <span className={styles.label}>STAFF</span>
                                          <span className={styles.name}>{staff}</span>
                                      </div>
                                      <div className={styles.metaItem}>
                                          <span className={styles.label}>VEHICLE</span>
                                          <span className={styles.name}>{vehicleName}</span>
                                      </div>
                                  </div>
                              </div>
                          );
                      })
                    : // single transport
                      (() => {
                          const raw = appointment.raw?.appointment || {};
                          const type = raw.details?.transportation?.type;
                          const timePickup = formatRange({ appointment: raw });
                          const vehicleName = raw.details?.transportation?.vehicleId
                              ? resolveVehicleName(raw.details?.transportation?.vehicleId)
                              : 'Unassigned';

                          return (
                              <div>
                                  <p className={styles.label}>
                                      {type === 'pickup' ? 'Pick Up' : type === 'dropoff' ? 'Drop Off' : 'Transport'}
                                  </p>
                                  <p className={styles.time}>{timePickup}</p>
                                  <p className={styles.location}>{service}</p>
                                  <div className={styles.meta}>
                                      <div className={styles.metaItem}>
                                          <span className={styles.label}>STAFF</span>
                                          <span className={styles.name}>{staff}</span>
                                      </div>
                                      <div className={styles.metaItem}>
                                          <span className={styles.label}>VEHICLE</span>
                                          <span className={styles.name}>{vehicleName}</span>
                                      </div>
                                  </div>
                              </div>
                          );
                      })()}
            </div>
        </div>
    );
};

export default PrintAppointmentTransportation;
